import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { EditOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  notification,
  Row,
  Upload,
  Input,
  Select,
  InputNumber,
  Card,
} from "antd";
import { UploadChangeParam } from "antd/lib/upload/interface";
import ContentTitle from "../../../components/ContentTitle/ContentTitle";
import { selectPerfume } from "../../../redux-toolkit/perfume/perfume-selector";
import {
  selectIsAdminStateLoading,
  selectIsPerfumeEdited,
} from "../../../redux-toolkit/admin/admin-selector";
import { LoadingStatus } from "../../../types/types";
import {
  resetAdminState,
  setAdminLoadingState,
} from "../../../redux-toolkit/admin/admin-slice";
import { fetchPerfume } from "../../../redux-toolkit/perfume/perfume-thunks";
import { updatePerfume } from "../../../redux-toolkit/admin/admin-thunks";
import IconButton from "../../../components/IconButton/IconButton";
import "./EditPerfume.css";

const { TextArea } = Input;
const { Option } = Select;

const categoryMap: Record<string, string[]> = {
  "Baby & Kids": ["Baby Clothes", "Toys", "Baby Soaps", "Baby Lotion", "Baby Powder", "Newborn Essentials", "Strollers & Cradles"],

  "Clothing & Fashion": ["Men's Clothing", "Women's Clothing", "Kids' Clothing", "Sportswear", "T-Shirts", "Sarees", "Dresses", "Fabrics", "Ethnic Wear", "Winter Wear"],

  "Furniture": ["Living Room Furniture", "Bedroom Furniture", "Office Furniture", "Kitchen Furniture", "Tables & Chairs", "Cupboards", "Sofas", "Storage Solutions"],

  "Kitchen & Home": ["Cookware", "Appliances", "Utensils", "Storage Containers", "Tableware", "Steel Pots", "Gas Stoves", "Chimneys"],

  "Industrial & Machinery": ["Agricultural Machinery", "Roti Maker Machines", "Milk Making Products", "Printing Machines", "Xerox Machines", "Toilet Paper Machines", "Flour Mill Machines", "Paper Plate Machines", "Water Purification Machines", "Steel Industries", "Fabrication Equipment"],

  "Jewellery & Tools": ["Jewellery Making Tools", "Polishing Machines", "Casting Equipment", "Moulding Tools", "Cleaning Machines"],

  "Electronics & Appliances": ["Televisions", "Air Conditioners", "Refrigerators", "Fans", "Coolers", "Mixies", "Grinders", "Mobile Phones", "Laptops", "Mobile Accessories", "Smart Watches", "Headphones"],

  "Automobile": ["Car Accessories", "Motorbike Gear", "Tyres", "Car Care", "Lubricants", "Vehicle Parts"],

  "Beauty & Personal Care": ["Perfumes", "Makeup", "Skincare", "Haircare", "Nail Polish", "Body Lotions", "Beauty Tools"],

  "Sports & Fitness": ["Gym Equipment", "Sportswear", "Footwear", "Outdoor Gear", "Accessories", "Bicycles"],

  "Books & Stationery": ["Fiction", "Non-Fiction", "Educational", "Children Books", "Comics", "Office Stationery", "Art & Craft Supplies"],

  "Apparency & Gifts": ["Gift Photo Frames", "Brass Materials", "Jewellery (Gold Plated / Imitation)", "Bangles", "Trophies", "Decor Items", "Showpieces", "Corporate Gifts"],
};

const mainCategories = Object.keys(categoryMap).sort();

const EditPerfume: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const params = useParams<{ id: string }>();
  const perfumeData = useSelector(selectPerfume);
  const isLoading = useSelector(selectIsAdminStateLoading);
  const isPerfumeEdited = useSelector(selectIsPerfumeEdited);
  const [file, setFile] = useState<File | null>(null);
  const [subCategories, setSubCategories] = useState<string[]>([]);

  useEffect(() => {
    dispatch(setAdminLoadingState(LoadingStatus.LOADED));
    dispatch(fetchPerfume(params.id));

    return () => {
      dispatch(resetAdminState(LoadingStatus.LOADING));
    };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (perfumeData) {
      form.setFieldsValue({
        ...perfumeData,
        actualPrice: perfumeData.actualPrice || perfumeData.price,
      });

      // ✅ Preload subcategories based on perfumeData.category
      if (perfumeData.category && categoryMap[perfumeData.category]) {
        setSubCategories([...categoryMap[perfumeData.category]].sort());
      }
    }
  }, [perfumeData]);

  useEffect(() => {
    if (isPerfumeEdited) {
      window.scrollTo(0, 0);
      notification.success({
        message: "Product Updated",
        description: "Product details updated successfully!",
      });
      dispatch(resetAdminState(LoadingStatus.SUCCESS));
    }
  }, [isPerfumeEdited]);

  const handleMainCategoryChange = (value: string) => {
    const subs = categoryMap[value] ? [...categoryMap[value]].sort() : [];
    setSubCategories(subs);
    form.setFieldsValue({ subCategory: undefined });
  };

  const onFormSubmit = (data: any): void => {
    const bodyFormData = new FormData();
    if (file) bodyFormData.append("file", file);
    const payload = { ...data, id: perfumeData?.id };
    bodyFormData.append(
      "perfume",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );
    dispatch(updatePerfume(bodyFormData));
  };

  const handleUpload = ({ file }: UploadChangeParam<any>): void => setFile(file);

  const handleDiscountOrPriceChange = () => {
    const actualPrice = form.getFieldValue("actualPrice") || 0;
    const discount = form.getFieldValue("discount") || 0;
    const discountedPrice = Math.round(actualPrice - (actualPrice * discount) / 100);
    form.setFieldsValue({ price: discountedPrice });
  };

  return (
    <div className="edit-container">
      <ContentTitle title="Edit Product" titleLevel={4} icon={<EditOutlined />} />

      <Card className="edit-card">
        <Form layout="vertical" form={form} onFinish={onFormSubmit}>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={14}>
              <Form.Item label="Product Title" name="perfumeTitle" rules={[{ required: true }]}>
                <Input placeholder="Enter product title" />
              </Form.Item>

              <Form.Item label="Brand" name="brand" rules={[{ required: true }]}>
                <Input placeholder="Enter brand name" />
              </Form.Item>

              {/* ✅ Dropdowns */}
              <Form.Item label="Main Category" name="category" rules={[{ required: true }]}>
                <Select placeholder="Select main category" onChange={handleMainCategoryChange}>
                  {mainCategories.map((cat) => (
                    <Option key={cat} value={cat}>
                      {cat}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Sub Category" name="subCategory" rules={[{ required: true }]}>
                <Select placeholder="Select subcategory" disabled={!subCategories.length}>
                  {subCategories.map((sub) => (
                    <Option key={sub} value={sub}>
                      {sub}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Gender" name="gender">
                    <Select placeholder="Select">
                      <Option value="male">Male</Option>
                      <Option value="female">Female</Option>
                      <Option value="unisex">Unisex</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Type" name="type">
                    <Input placeholder="Spray, Roll-on..." />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Unit Type" name="unit">
                    <Input placeholder="e.g. ml, pcs" />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Quantity" name="quantity" rules={[{ required: true }]}>
                    <InputNumber min={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item label="Min Quantity" name="minQuantity" rules={[{ required: true }]}>
                    <InputNumber min={1} style={{ width: "100%" }} placeholder="Min order quantity" />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label="Max Quantity" name="maxQuantity" rules={[{ required: true }]}>
                    <InputNumber min={1} style={{ width: "100%" }} placeholder="Max order quantity" />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label="Shipping Charges (%)" name="shipPer" rules={[{ required: true }]}>
                    <InputNumber min={1} max={100} style={{ width: "100%" }} placeholder="Shipping %" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Specifications" name="specifications">
                <TextArea rows={2} placeholder="Enter product specs" />
              </Form.Item>

              <Form.Item label="Seller Name" name="sellerName">
                <Input placeholder="Enter seller name" />
              </Form.Item>

              <Form.Item label="Status" name="status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="draft">Draft</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Delivery Timeline" name="deliveryTimeline">
                <Select placeholder="Select delivery timeline">
                  <Option value="2 Days">2 Days</Option>
                  <Option value="5 Days">5 Days</Option>
                  <Option value="7 Days">7 Days</Option>
                  <Option value="15 Days">15 Days</Option>
                  <Option value="30 Days">30 Days</Option>
                </Select>
              </Form.Item>

              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Actual Price (₹)" name="actualPrice">
                    <InputNumber min={0} style={{ width: "100%" }} onChange={handleDiscountOrPriceChange} />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Discount (%)" name="discount">
                    <InputNumber min={0} max={99} style={{ width: "100%" }} onChange={handleDiscountOrPriceChange} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="Final Price (₹)" name="price" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item label="Tags" name="tags">
                <Input placeholder="Comma-separated tags" />
              </Form.Item>

              <Form.Item label="Description" name="description" rules={[{ required: true }]}>
                <TextArea rows={4} placeholder="Enter product description" />
              </Form.Item>
            </Col>

            <Col xs={24} md={10}>
              <div className="upload-section">
                <Upload name="file" onChange={handleUpload} beforeUpload={() => false}>
                  <Button icon={<UploadOutlined />}>Upload Image</Button>
                </Upload>

                {perfumeData?.filename && (
                  <div className="preview-box">
                    <img src={perfumeData.filename} alt={perfumeData.perfumeTitle} className="preview-image" />
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <div className="submit-section">
            <IconButton title="Update Product" icon={<EditOutlined />} disabled={isLoading} />
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default EditPerfume;
